import type { ClipStatusTimeline, TimelineEntry } from "./@types";
import { OverallState, HAPPY_PATH_EVENTS } from "./@types";

export const STATE_COPY: Record<string, string> = {
  REQUESTED: "Starting your clip",
  CREATING: "Building your clip",
  SUBMITTED: "In the queue",
  QUEUED: "Almost there, waiting to record",
  CAPTURING: "Recording your gameplay",
  COMPLETED: "Your clip is ready!",
  COMPLETED_DESKTOP: "Your clip is ready!",
  COMPLETED_MOBILE: "Mobile ready, finishing desktop",
};

export const ERROR_COPY: Record<string, string> = {
  ERR_VALIDATION: "Something didn't look right, validation failed",
  ERR_CREATION: "Hit a snag creating your clip",
  ERR_CAPTURE: "Couldn't finish recording",
  ERR_TIMEOUT: "This one took too long and timed out",
};

export const isRetrying = (events: TimelineEntry[]): boolean => {
  let submittedCount = 0;
  for (const entry of events) {
    if (entry.event === "SUBMITTED" && !entry.isMobile) submittedCount++;
  }
  return submittedCount >= 2;
};

export const resolveActiveIndex = (events: TimelineEntry[]): number => {
  const reached = new Set(
    events.filter((e) => !e.isMobile).map((e) => e.event),
  );
  let highest = -1;
  for (let i = 0; i < HAPPY_PATH_EVENTS.length; i++) {
    if (reached.has(HAPPY_PATH_EVENTS[i])) highest = i;
  }
  return highest;
};

export const MAX_STEP_IDX = HAPPY_PATH_EVENTS.length - 1;

const MIN_STEP_S = 6;
const FALLBACK_STEP_S = 15;

export const getSegmentDuration = (segIdx: number, eta?: string): number => {
  if (segIdx < 3) return MIN_STEP_S;
  if (eta) {
    const msLeft = Math.max(0, new Date(eta).getTime() - Date.now());
    const segsLeft = 5 - segIdx;
    return Math.max(MIN_STEP_S, msLeft / 1000 / segsLeft);
  }
  return FALLBACK_STEP_S;
};

// Hold just short of full so the bar never claims completion before it lands.
const ACTIVE_FILL_CAP = 0.97;

// While CAPTURING, fill the connector by elapsed-within-stage against the ETA
// so it tracks the same clock as the "X-Y min" text. Null elsewhere / no ETA.
export const getCapturingFill = (
  activeIdx: number,
  events: TimelineEntry[],
  eta?: string,
): number | null => {
  if (activeIdx < 0 || !eta) return null;
  if (HAPPY_PATH_EVENTS[activeIdx] !== "CAPTURING") return null;
  const startedAt = events.find((e) => !e.isMobile && e.event === "CAPTURING")
    ?.at;
  if (!startedAt) return null;
  const startMs = new Date(startedAt).getTime();
  const etaMs = new Date(eta).getTime();
  if (!(etaMs > startMs)) return ACTIVE_FILL_CAP;
  const frac = (Date.now() - startMs) / (etaMs - startMs);
  return Math.min(ACTIVE_FILL_CAP, Math.max(0, frac));
};

// Fragmovies/montages can legitimately spend ~30 min recording + post-processing
// between events, so only flag "taking longer than usual" past that.
export const STALE_THRESHOLD_MS = 35 * 60 * 1000;

export const isTimelineComplete = (timeline: ClipStatusTimeline): boolean =>
  timeline.overallState === OverallState.COMPLETED ||
  timeline.overallState === OverallState.COMPLETED_DESKTOP;

export const isTimelineErrored = (timeline: ClipStatusTimeline): boolean =>
  timeline.overallState === OverallState.ERRORED;

export const isTimelineStale = (timeline: ClipStatusTimeline): boolean =>
  !isTimelineErrored(timeline) &&
  !isTimelineComplete(timeline) &&
  !!timeline.lastEventAt &&
  Date.now() - new Date(timeline.lastEventAt).getTime() > STALE_THRESHOLD_MS;

export const getStatusCopy = (timeline: ClipStatusTimeline): string => {
  const errorCode = timeline.desktop?.errorCode ?? timeline.mobile?.errorCode;
  if (isRetrying(timeline.events)) {
    return "Re-rendering, this sometimes happens, hang tight";
  }
  if (isTimelineStale(timeline)) return "Taking longer than usual";
  if (isTimelineErrored(timeline)) {
    return ERROR_COPY[errorCode ?? ""] ?? "Processing error";
  }
  return (
    STATE_COPY[HAPPY_PATH_EVENTS[resolveActiveIndex(timeline.events)]] ??
    "Processing"
  );
};

const GAME_TO_SUPPORT_TAG: Record<number | string, string> = {
  7302: "cs2",
  730: "csgo",
  570: "dota2",
  704: "fortnite",
  101: "league_of_legends",
  NONE: "none",
};

export const buildSupportLink = (
  timeline: ClipStatusTimeline,
  userId?: string,
): string => {
  const profileLink = `https://allstar.gg/profile?user=${userId}`;
  const gameTag = GAME_TO_SUPPORT_TAG[timeline.game ?? "NONE"] ?? "none";
  return `https://help.allstar.gg/hc/en-us/requests/new?ticket_form_id=12129622526487&tf_12391937440663=${timeline.trackId}&tf_12579506242199=${profileLink}&tf_12070892016151=${gameTag}&tf_12153827873687=errored_clip`;
};
